/**
 * Short-lived, in-memory route context. Never store applicant names, IDs,
 * search text or tenant information in URLs, localStorage or sessionStorage.
 * The backend remains the authority when a profile is opened.
 */
const MAX_ROUTE_APPLICANTS = 250;

export function cleanApplicantRows(applicants) {
  const seen = new Set();
  return (Array.isArray(applicants) ? applicants : []).reduce((rows, item) => {
    const id = typeof item?.id === 'string' ? item.id.trim() : '';
    if (!id || seen.has(id)) return rows;
    seen.add(id);
    const name = [item.first_name, item.last_name]
      .filter((v) => typeof v === 'string' && v.trim())
      .map((v) => v.trim()).join(' ');
    rows.push({ id, name: name || (typeof item.name === 'string' && item.name.trim() ? item.name.trim() : 'Applicant') });
    return rows;
  }, []);
}

export function createApplicantNavigation(applicants, currentId, filters = {}) {
  const all = cleanApplicantRows(applicants);
  const currentIndex = all.findIndex((item) => item.id === currentId);
  const start = all.length > MAX_ROUTE_APPLICANTS
    ? Math.max(0, Math.min(currentIndex < 0 ? 0 : currentIndex - 125, all.length - MAX_ROUTE_APPLICANTS))
    : 0;
  const rows = all.slice(start, start + MAX_ROUTE_APPLICANTS);
  return {
    version: 1,
    applicants: rows,
    incomplete: all.length > rows.length,
    filters: {
      search: typeof filters.search === 'string' ? filters.search : '',
      statusFilter: typeof filters.statusFilter === 'string' ? filters.statusFilter : '',
      focusFilter: typeof filters.focusFilter === 'string' ? filters.focusFilter : '',
    },
  };
}

export function readApplicantNavigation(value) {
  if (!value || value.version !== 1 || !Array.isArray(value.applicants)) return null;
  return {
    version: 1,
    applicants: cleanApplicantRows(value.applicants).slice(0, MAX_ROUTE_APPLICANTS),
    incomplete: value.incomplete === true,
    filters: {
      search: typeof value.filters?.search === 'string' ? value.filters.search : '',
      statusFilter: typeof value.filters?.statusFilter === 'string' ? value.filters.statusFilter : '',
      focusFilter: typeof value.filters?.focusFilter === 'string' ? value.filters.focusFilter : '',
    },
  };
}

export function adjacentApplicantIds(applicants, currentId) {
  const index = (applicants || []).findIndex((item) => item.id === currentId);
  return {
    position: index < 0 ? null : index + 1,
    total: (applicants || []).length,
    previous: index > 0 ? applicants[index - 1].id : null,
    next: index >= 0 && index < applicants.length - 1 ? applicants[index + 1].id : null,
  };
}
